<template>
    <sidebar @close="$emit('close')">
        <template v-slot:title>
            {{ integration.client.name }}
        </template>

        <template v-slot:header>
            <div class="row mb-1">
                <div class="col">
                    <span class="text-muted">Client {{ integration.client.client_code }}</span>
                </div>
            </div>
        </template>

        <slot>
            <div class="card mb-2">
                <div class="card-header pr-1 d-flex align-items-center">
                    <span class="btnlike mr-auto">
                        <h4 class="mb-0">Integration Info</h4>
                    </span>

                    <button class="btn btn-outline-primary" type="button" @click.prevent="$refs.editModal.open()">
                        <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                        Edit
                    </button>
                </div>

                <div class="card-body py-1">
                    <div class="mui-textfield">
                        <span class="textfield">{{ integration.get_pos_display }}</span>
                        <label>POS</label>
                    </div>

                    <div class="mui-textfield">
                        <span class="textfield">{{ integration.label }}</span>
                        <label>Label</label>
                    </div>

                    <div class="mui-textfield">
                        <span class="textfield">{{ integration.ref_code }}</span>
                        <label>Reference Code</label>
                    </div>

                    <div class="mui-textfield">
                        <span class="textfield" v-if="integration.apply_to_stores_default">Yes</span><span v-else>No</span>
                        <label>Default Integration</label>
                    </div>
                </div>
            </div>

            <div class="card mb-2" v-if="['lc', 'lc_alt', 'lc_alt_api', 'qsr', 'sicom', 'nbo', 'synergy', 'crunchtime', 'R365', 'micros_sonic', 'tcpplus', 'flexepos'].includes(integration.pos)">
                <div class="card-header pr-1 d-flex align-items-center">
                    <span class="btnlike mr-auto">
                        <h4 class="mb-0">POS Connection</h4>
                    </span>

                    <button class="btn btn-outline-primary" type="button" @click.prevent="$refs.connectionModal.open()">
                        <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                        Edit
                    </button>
                </div>

                <div class="card-body py-1">
                    <div class="mui-textfield" v-if="['lc', 'sicom', 'synergy', 'crunchtime', 'R365', 'flexepos'].includes(integration.pos)">
                        <span class="textfield">{{ integration.pos_url }}</span>
                        <label>URL</label>
                    </div>

                    <div class="mui-textfield" v-if="['lc', 'lc_alt', 'sicom', 'nbo', 'synergy', 'crunchtime', 'R365', 'micros_sonic', 'flexepos'].includes(integration.pos)">
                        <span class="textfield">{{ integration.pos_username }}</span>
                        <label>Username</label>
                    </div>

                    <div class="mui-textfield" v-if="['lc', 'lc_alt', 'sicom', 'nbo', 'synergy', 'crunchtime', 'R365', 'micros_sonic', 'flexepos'].includes(integration.pos)">
                        <span class="textfield">{{ integration.pos_password_masked }}</span>
                        <label>Password</label>
                    </div>

                    <div class="mui-textfield" v-if="['tcpplus'].includes(integration.pos)">
                        <span class="textfield">{{ integration.pos_password_masked }}</span>
                        <label>API Key</label>
                    </div>

                    <div class="mui-textfield d-flex align-items-end" v-if="integration.inbound_api_key_masked">
                        <span class="textfield flex-grow-1">{{ integration.inbound_api_key_masked }}</span>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary ml-2"
                            @click.prevent="copyInboundApiKey()"
                            :title="copiedInboundApiKey ? 'Copied!' : 'Copy full key to clipboard'"
                        >
                            <i class="far fa-copy" aria-hidden="true"></i>
                            {{ copiedInboundApiKey ? 'Copied' : 'Copy' }}
                        </button>
                        <label>Inbound API Key</label>
                    </div>

                    <div class="mui-textfield" v-if="['sicom'].includes(integration.pos)">
                        <span class="textfield">{{ integration.sicom_custom_report_id }}</span>
                        <label>SICOM Custom Report ID</label>
                    </div>

                    <div class="mui-textfield" v-if="['qsr'].includes(integration.pos)">
                        <span class="textfield">{{ integration.qsr_client_number }}</span>
                        <label>QSR Client Number</label>
                    </div>

                    <div class="mui-textfield" v-if="['qsr'].includes(integration.pos)">
                        <span class="textfield">{{ integration.qsr_mode }}</span>
                        <label>QSR Mode</label>
                    </div>

                    <div class="mui-textfield" v-if="['lc_alt_api'].includes(integration.pos)">
                        <span class="textfield">{{ integration.alt_api_env }}</span>
                        <label>API Environment</label>
                    </div>

                    <div class="mui-textfield" v-if="['lc_alt_api'].includes(integration.pos)">
                        <span class="textfield">{{ integration.pos_client_id }}</span>
                        <label>POS Client ID</label>
                    </div>

                </div>

                <div v-if="integration.pos_credentials.length > 0">
                    <h5 class="ml-2 mb-0" v-if="integration.pos_credentials">Additional Credentials</h5>

                    <div class="card card-body item-details p-2 m-2" v-for="credential in integration.pos_credentials">
                        <div class="mui-textfield">
                            <span class="textfield"><b>{{ credential.ref_code }}</b></span>
                            <label>Reference Code</label>
                        </div>

                        <div class="mui-textfield" v-if="['lc', 'sicom', 'crunchtime', 'R365'].includes(integration.pos)">
                            <span class="textfield">{{ credential.pos_url }}</span>
                            <label>URL</label>
                        </div>

                        <div class="mui-textfield" v-if="['lc', 'lc_alt', 'sicom', 'nbo', 'crunchtime', 'R365', 'micros_sonic'].includes(integration.pos)">
                            <span class="textfield">{{ credential.pos_username }}</span>
                            <label>Username</label>
                        </div>

                        <div class="mui-textfield" v-if="['lc', 'lc_alt', 'sicom', 'nbo', 'crunchtime', 'R365', 'micros_sonic'].includes(integration.pos)">
                            <span class="textfield">{{ credential.pos_password_masked }}</span>
                            <label>Password</label>
                        </div>

                        <div class="mui-textfield" v-if="credential.sync_only">
                            <span class="textfield">YES</span>
                            <label>Sync Only</label>
                        </div>

                    </div>
                </div>

                <div v-if="integration.pos_credentials_last_updated_on" class="card-body py-0 mb-2">
                    <small>
                        Updated {{ $filters.datetime(integration.pos_credentials_last_updated_on) }} by {{ integration.pos_credentials_last_updated_by.email }}
                    </small>
                </div>
            </div>

            <div class="card mb-2">
                <div class="card-header pr-1 d-flex align-items-center">
                    <span class="btnlike mr-auto">
                        <h4 class="mb-0">Hours Import</h4>
                    </span>

                    <button class="btn btn-outline-primary" type="button" @click.prevent="$refs.hoursImportModal.open()">
                        <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                        Edit
                    </button>
                </div>

                <div class="card-body py-1">
                    <div class="mui-textfield">
                        <span class="textfield" v-if="integration.has_hours_importer">Yes</span><span v-else>No</span>
                        <label>Enabled</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield">{{ integration.pos_default_company_code }}</span>
                        <label>Default company code</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield">{{ integration.pos_default_paygroup }}</span>
                        <label>Default paygroup</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield">{{ integration.get_matching_method_display }}</span>
                        <label>Matching Method</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield" v-if="integration.pos_do_mismatch_check">Yes</span><span v-else>No</span>
                        <label>Check for Name Mismatches</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield" v-if="integration.pos_use_payrate_in_hours_import">Yes</span><span v-else>No</span>
                        <label>Use Rate of Pay from POS</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer && ['lc', 'qsr', 'sicom'].includes(integration.pos)">
                        <span class="textfield" v-if="integration.pos_check_floaters">Yes</span><span v-else>No</span>
                        <label>Flag Floater OT</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer && ['lc', 'qsr', 'sicom', 'rti'].includes(integration.pos)">
                        <span class="textfield" v-if="integration.pos_ignore_salaried_hours">Yes</span><span v-else>No</span>
                        <label>Ignore Salaried Hours</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer && ['qsr'].includes(integration.pos)">
                        <span class="textfield">{{ integration.qsr_mischrs_earning_code }}</span>
                        <label>Misc Hrs Earning Code</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer && ['qsr'].includes(integration.pos)">
                        <span class="textfield">{{ integration.qsr_miscamt_earning_code }}</span>
                        <label>Misc Amt</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer && ['qsr'].includes(integration.pos)">
                        <span class="textfield">{{ integration.qsr_diffhours_earning_code }}</span>
                        <label>Diff Hrs Earning Code</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer && ['qsr'].includes(integration.pos)">
                        <span class="textfield">{{ integration.qsr_diffamt_earning_code }}</span>
                        <label>Diff Amt Earning Code</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer && ['lc_alt'].includes(integration.pos)">
                        <span class="textfield" v-if="integration.pos_only_posted_hours">Yes</span><span v-else>No</span>
                        <label>Only import posted hours</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer && ['lc_alt_api'].includes(integration.pos)">
                        <span class="textfield" v-if="integration.pos_allow_upload">Yes</span><span v-else>No</span>
                        <label>Allow File Upload option</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer && ['tcpplus'].includes(integration.pos)">
                        <span class="textfield">{{ integration.tcpplus_tips_earning_code }}</span>
                        <label>Tips Earning Code</label>
                    </div>

                    <h5 class="mt-2 mb-2" v-if="integration.has_hours_importer">Org Level Mappings</h5>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield">{{ integration.org_level_1_pos_hours_import_field }}</span>
                        <label>Org Level 1</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield">{{ integration.org_level_2_pos_hours_import_field }}</span>
                        <label>Org Level 2</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield">{{ integration.org_level_3_pos_hours_import_field }}</span>
                        <label>Org Level 3</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield">{{ integration.org_level_4_pos_hours_import_field }}</span>
                        <label>Org Level 4</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.has_hours_importer">
                        <span class="textfield">{{ integration.org_level_5_pos_hours_import_field }}</span>
                        <label>Org Level 5</label>
                    </div>
                </div>
            </div>

            <div class="card mb-2" v-if="['qsr', 'sicom'].includes(integration.pos)">
                <div class="card-header pr-1 d-flex align-items-center">
                    <span class="btnlike mr-auto">
                        <h4 class="mb-0">New Hires Import</h4>
                    </span>

                    <button class="btn btn-outline-primary" type="button" @click.prevent="$refs.newHireImportModal.open()">
                        <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                        Edit
                    </button>
                </div>

                <div class="card-body py-1">
                    <div class="mui-textfield">
                        <span class="textfield" v-if="integration.pos_do_new_hires">Yes</span><span v-else>No</span>
                        <label>Enabled</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_do_new_hires">
                        <span class="textfield">{{ integration.get_default_pay_frequency_display }}</span>
                        <label>Default pay frequency (overriden by Paygroup)</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_do_new_hires">
                        <span class="textfield">{{ integration.default_normal_hours }}</span>
                        <label>Default normal hours (overriden by Paygroup)</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_do_new_hires">
                        <span class="textfield">{{ integration.payrate_warning_threshold }}</span>
                        <label>Pay rate warning threshold</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_do_new_hires">
                        <span class="textfield" v-if="integration.pos_do_addl_withholding_check">Yes</span><span v-else>No</span>
                        <label>Flag Additional Withholding Elections</label>
                    </div>


                    <h5 class="mt-2 mb-2" v-if="integration.pos_do_new_hires">Org Level Mappings</h5>

                    <div class="mui-textfield" v-if="integration.pos_do_new_hires">
                        <span class="textfield">{{ integration.org_level_1_pos_employee_import_field }}</span>
                        <span class="textfield pl-2" v-if="integration.org_level_1_pos_employee_import_field_default"><b>Default:</b> {{ integration.org_level_1_pos_employee_import_field_default }}</span>
                        <label>Org Level 1</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_do_new_hires">
                        <span class="textfield">{{ integration.org_level_2_pos_employee_import_field }}</span>
                        <span class="textfield pl-2" v-if="integration.org_level_2_pos_employee_import_field_default"><b>Default:</b> {{ integration.org_level_2_pos_employee_import_field_default }}</span>
                        <label>Org Level 2</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_do_new_hires">
                        <span class="textfield">{{ integration.org_level_3_pos_employee_import_field }}</span>
                        <span class="textfield pl-2" v-if="integration.org_level_3_pos_employee_import_field_default"><b>Default:</b> {{ integration.org_level_3_pos_employee_import_field_default }}</span>
                        <label>Org Level 3</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_do_new_hires">
                        <span class="textfield">{{ integration.org_level_4_pos_employee_import_field }}</span>
                        <span class="textfield pl-2" v-if="integration.org_level_4_pos_employee_import_field_default"><b>Default:</b> {{ integration.org_level_4_pos_employee_import_field_default }}</span>
                        <label>Org Level 4</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_do_new_hires">
                        <span class="textfield">{{ integration.org_level_5_pos_employee_import_field }}</span>
                        <span class="textfield pl-2" v-if="integration.org_level_5_pos_employee_import_field_default"><b>Default:</b> {{ integration.org_level_5_pos_employee_import_field_default }}</span>
                        <label>Org Level 5</label>
                    </div>
                </div>
            </div>

            <div class="card mb-2" v-if="['lc', 'lc_alt', 'lc_alt_api', 'synergy', 'crunchtime', 'micros_sonic', 'tcpplus', 'flexepos'].includes(integration.pos)">
                <div class="card-header pr-1 d-flex align-items-center">
                    <span class="btnlike mr-auto">
                        <h4 class="mb-0">New Hire Sync (to POS)</h4>
                    </span>

                    <button class="btn btn-outline-primary" type="button" @click.prevent="$refs.newHirePOSSyncModal.open()">
                        <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                        Edit
                    </button>
                </div>

                <div class="card-body py-1">
                    <div class="mui-textfield">
                        <span class="textfield" v-if="integration.pos_new_hire_sync">Yes</span><span v-else>No</span>
                        <label>Enabled</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_new_hire_sync && integration.new_hires_use_api">
                        <span class="textfield" v-if="integration.new_hires_use_api">Yes</span><span v-else>No</span>
                        <label>Use API for New Hire Sync</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_new_hire_sync && ['crunchtime'].includes(integration.pos)">
                        <span class="textfield" v-if="integration.pos_sync_ssn">Yes</span><span v-else>No</span>
                        <label>Send SSN to POS</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_new_hire_sync">
                        <span class="textfield">{{ integration.pos_new_hire_default_position }}</span>
                        <label>{{ ['tcpplus'].includes(integration.pos) ? 'Default Job Code' : 'POS Default Position' }}</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_new_hire_sync">
                        <span class="textfield">
                            <div v-if="integration.digest_notification_emails.length > 0">
                                <template v-for="email in integration.digest_notification_emails" :key="email">
                                    {{ email }}<br>
                                </template>
                            </div>
                            <div v-else>
                                Default (System Admins)
                            </div>
                        </span>
                        <label>Digest Notification Recipients</label>
                    </div>

                    <div class="mui-textfield" v-if="integration.pos_new_hire_sync">
                        <span class="textfield">
                            <div v-if="integration.error_notification_emails.length > 0">
                                <template v-for="email in integration.error_notification_emails" :key="email">
                                    {{ email }}<br>
                                </template>
                            </div>
                            <div v-else>
                                Default (System Admins)
                            </div>
                        </span>
                        <label>Error Notification Recipients</label>
                    </div>
                </div>
            </div>

            <div class="card mb-2" v-if="integration.pos === 'tcpplus'">
                <div class="card-header pr-1 d-flex align-items-center">
                    <span class="btnlike mr-auto">
                        <h4 class="mb-0">TCP+ Diagnostics</h4>
                    </span>
                </div>

                <div class="card-body py-2">
                    <p class="text-muted small mb-2">
                        Read-only inspection of the TCP+ tenant. Useful for sanity-checking
                        configuration before pushing employees.
                    </p>
                    <button class="btn btn-outline-primary btn-block mb-2" type="button"
                            @click.prevent="$refs.tcpplusJobcodesModal.open()">
                        View Job Codes
                    </button>
                    <button class="btn btn-outline-primary btn-block mb-2" type="button"
                            @click.prevent="$refs.tcpplusLocationsModal.open()">
                        View Locations
                    </button>
                    <button class="btn btn-outline-primary btn-block mb-2" type="button"
                            @click.prevent="$refs.tcpplusRolesModal.open()">
                        View Employee Roles
                    </button>
                    <button class="btn btn-outline-primary btn-block" type="button"
                            @click.prevent="$refs.tcpplusBulkSyncModal.open()">
                        Bulk Sync (Company or Store)
                    </button>
                </div>
            </div>

            <pos-edit-modal v-if="integration" ref="editModal" :integration="integration" :client="client" @updated="$emit('updated', $event)" />

            <pos-connection-modal v-if="integration" ref="connectionModal" :integration="integration" :client="client" @updated="$emit('updated', $event)" />

            <pos-hours-import-modal v-if="integration" ref="hoursImportModal" :integration="integration" :client="client" @updated="$emit('updated', $event)" />

            <pos-new-hire-import-modal v-if="integration" ref="newHireImportModal" :integration="integration" :client="client" @updated="$emit('updated', $event)" />

            <pos-new-hire-pos-sync-modal v-if="integration" ref="newHirePOSSyncModal" :integration="integration" :client="client" @updated="$emit('updated', $event)" />

            <pos-tcpplus-jobcodes-modal v-if="integration && integration.pos === 'tcpplus'" ref="tcpplusJobcodesModal" :integration="integration" :client="client" />

            <pos-tcpplus-locations-modal v-if="integration && integration.pos === 'tcpplus'" ref="tcpplusLocationsModal" :integration="integration" :client="client" />

            <pos-tcpplus-roles-modal v-if="integration && integration.pos === 'tcpplus'" ref="tcpplusRolesModal" :integration="integration" :client="client" />

            <pos-tcpplus-bulk-sync-modal v-if="integration && integration.pos === 'tcpplus'" ref="tcpplusBulkSyncModal" :integration="integration" :client="client" />

            <button class="btn btn-outline-primary btn-block my-2" @click.prevent="inactivate()" v-if="integration.is_active">Deactivate Integration</button>
            <button class="btn btn-outline-primary btn-block my-2" @click.prevent="reactivate()" v-if="!integration.is_active">Reactivate Integration</button>
        </slot>
    </sidebar>
</template>

<script>
import POSAddEditIntegrationModal from './POSAddEditIntegrationModal'
import POSConnectionModal from './POSConnectionModal'
import POSHoursImportModal from './POSHoursImportModal'
import POSNewHireImportModal from './POSNewHireImportModal'
import POSNewHirePOSSyncModal from './POSNewHirePOSSyncModal'
import POSTcpplusJobcodesModal from './POSTcpplusJobcodesModal'
import POSTcpplusLocationsModal from './POSTcpplusLocationsModal'
import POSTcpplusRolesModal from './POSTcpplusRolesModal'
import POSTcpplusBulkSyncModal from './POSTcpplusBulkSyncModal'
import Sidebar from '@/components/Sidebar'

export default {
    components: {
        PosEditModal: POSAddEditIntegrationModal,
        PosConnectionModal: POSConnectionModal,
        PosHoursImportModal: POSHoursImportModal,
        PosNewHireImportModal: POSNewHireImportModal,
        PosNewHirePosSyncModal: POSNewHirePOSSyncModal,
        PosTcpplusJobcodesModal: POSTcpplusJobcodesModal,
        PosTcpplusLocationsModal: POSTcpplusLocationsModal,
        PosTcpplusRolesModal: POSTcpplusRolesModal,
        PosTcpplusBulkSyncModal: POSTcpplusBulkSyncModal,
        Sidebar,
    },
    props: ['integration', 'client', ],
    data() {
        return {
            copiedInboundApiKey: false,
        }
    },
    methods: {
        copyInboundApiKey() {
            if (!this.integration.inbound_api_key) return
            navigator.clipboard.writeText(this.integration.inbound_api_key).then(() => {
                this.copiedInboundApiKey = true
                setTimeout(() => { this.copiedInboundApiKey = false }, 2000)
            })
        },
        reactivate() {
            if (!confirm('Are you sure?')) {return}

            this.$store.dispatch('START_LOADING')
            this.$api.put(`/admin/clients/${this.client.id}/pos-integrations/${this.integration.id}/status`, {status: 'active'}).then(resp => {
                this.$emit('updated', resp)
                this.$store.dispatch('STOP_LOADING')
                this.$emit('close')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(this.$bus.firstError(errors))
            })
        },
        inactivate() {
            if (!confirm('Are you sure?')) {return}

            this.$store.dispatch('START_LOADING')
            this.$api.put(`/admin/clients/${this.client.id}/pos-integrations/${this.integration.id}/status`, {status: 'inactive'}).then(resp => {
                this.$emit('updated', resp)
                this.$store.dispatch('STOP_LOADING')
                this.$emit('close')
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
    }
}
</script>
